import {
  Box,
  Button,
  ButtonBase,
  ButtonGroup,
  Card,
  CardContent,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FormEvent, useEffect, useRef, useState } from "react";
import { FileSpreadsheet } from "lucide-react";
import { financeApi } from "../lib/api";
import { useBlockedAccessGuard } from "../hooks/useBlockedAccessGuard";
import { fallbackCurrencies } from "../lib/labels";
import { memberAvatarColor, memberInitials } from "../lib/avatar";
import { CategoryIcon, money } from "../lib/format";
import { IconColorPicker } from "../components/IconColorPicker";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { SidePanel } from "../components/SidePanel";
import type { Account, AccountPayload, Currency, Group, Member } from "../lib/types";
import { queryKeys } from "../lib/queryKeys";

type BankAccountsPageProps = {
  group: Group;
  onBlockedAccess?: () => void;
};

type AccountsMode = "active" | "deleted";

type ModalState =
  | { kind: "closed" }
  | { kind: "create" }
  | { kind: "edit"; account: Account };

type AccountFormErrors = {
  name?: string;
  initial_balance?: string;
  current_amount?: string;
  currency?: string;
  owner_user_id?: string;
};

const accountTypeDefaults: Record<string, { icon: string; color: string; label: string }> = {
  bank: { icon: "boc", color: "#2563eb", label: "Bank" },
  card: { icon: "wallet", color: "#7c3aed", label: "Card" },
  cash: { icon: "cash", color: "#2563eb", label: "Cash" },
  savings: { icon: "safe", color: "#b7791f", label: "Savings" },
  debt: { icon: "coins", color: "#b42318", label: "Debt" },
};

const accountTypes = Object.entries(accountTypeDefaults).map(([value, config]) => ({ value, label: config.label }));

function currencyLabel(code: string, currencies: Currency[]) {
  const option = currencies.find((item) => item.code === code);
  return option ? `${option.symbol} ${option.code}` : code;
}

function MemberAvatar({ member }: { member: Member }) {
  return (
    <span className={`account-owner-avatar${member.avatar_data_url ? " has-image" : ""}`} style={{ backgroundColor: member.avatar_data_url ? undefined : memberAvatarColor(member) }}>
      {member.avatar_data_url ? <img src={member.avatar_data_url} alt="" /> : memberInitials(member)}
    </span>
  );
}

function AccountOwnerOption({ member }: { member: Member }) {
  return (
    <span className="records-select-option account-owner-option">
      <MemberAvatar member={member} />
      <span>{member.display_name}</span>
    </span>
  );
}

function parseMoneyInput(value: FormDataEntryValue | null) {
  const text = String(value || "").trim().replace(",", ".");
  return { text, amount: Number(text) };
}

function buildAccountPayload(form: HTMLFormElement, type: string, icon: string, color: string, isEditing: boolean): { payload?: AccountPayload; error?: string } {
  const data = new FormData(form);
  const name = String(data.get("name") || "").trim();
  if (!name) return { error: "Name is required." };
  const currency = String(data.get("currency") || "").trim().toUpperCase();
  if (!currency) return { error: "Currency is required." };
  const ownerUserId = String(data.get("owner_user_id") || "").trim();
  if (!ownerUserId) return { error: "Owner is required." };
  const payload: AccountPayload = { name, type, icon, color, currency, owner_user_id: ownerUserId };
  if (!isEditing) {
    const { text: initialText, amount: initialBalance } = parseMoneyInput(data.get("initial_balance"));
    if (!initialText || !Number.isFinite(initialBalance)) return { error: "Initial balance must be a number." };
    payload.initial_balance = initialBalance.toFixed(2);
  } else {
    const { text: currentText, amount: currentAmount } = parseMoneyInput(data.get("current_amount"));
    if (!currentText || !Number.isFinite(currentAmount)) return { error: "Current amount must be a number." };
    payload.current_amount = currentAmount.toFixed(2);
  }
  return { payload };
}

function validateAccountForm(form: HTMLFormElement, isEditing: boolean): AccountFormErrors {
  const data = new FormData(form);
  const errors: AccountFormErrors = {};
  if (!String(data.get("name") || "").trim()) errors.name = "Name is required.";
  if (!String(data.get("currency") || "").trim()) errors.currency = "Currency is required.";
  if (!String(data.get("owner_user_id") || "").trim()) errors.owner_user_id = "Owner is required.";
  if (!isEditing) {
    const { text: initialText, amount: initialBalance } = parseMoneyInput(data.get("initial_balance"));
    if (!initialText) {
      errors.initial_balance = "Initial balance is required.";
    } else if (!Number.isFinite(initialBalance)) {
      errors.initial_balance = "Enter a valid number.";
    }
  } else {
    const { text: currentText, amount: currentAmount } = parseMoneyInput(data.get("current_amount"));
    if (!currentText) {
      errors.current_amount = "Current amount is required.";
    } else if (!Number.isFinite(currentAmount)) {
      errors.current_amount = "Enter a valid number.";
    }
  }
  return errors;
}

function hasAccountErrors(errors: AccountFormErrors) {
  return Boolean(errors.name || errors.initial_balance || errors.current_amount || errors.currency || errors.owner_user_id);
}

function AccountRow({
  account,
  balance,
  owner,
  currencies,
  onClick,
}: {
  account: Account;
  balance: string;
  owner?: Member;
  currencies: Currency[];
  onClick: () => void;
}) {
  const defaults = accountTypeDefaults[account.type] || accountTypeDefaults.bank;
  const balanceValue = Number(balance || 0);
  const balanceClass = balanceValue < 0 ? "amount-expense" : "amount-income";
  return (
    <ButtonBase className={`category-row account-row account-${account.type}${account.is_archived ? " archived" : ""}`} type="button" onClick={onClick}>
      <Box className="category-item-main">
        <CategoryIcon icon={account.icon || defaults.icon} color={account.color || defaults.color} />
        <Box className="category-copy">
          <strong>{account.name}</strong>
          <span>
            {account.is_archived ? "Deleted · " : ""}{defaults.label} · {currencyLabel(account.currency, currencies)}
            {owner ? ` · ${owner.display_name}` : ""}
          </span>
        </Box>
      </Box>
      <strong className={`account-balance ${balanceClass}`}>{money(balance)}</strong>
    </ButtonBase>
  );
}

function AccountModal({
  state,
  group,
  members,
  membersLoading,
  currencies,
  currentBalance,
  onClose,
}: {
  state: Exclude<ModalState, { kind: "closed" }>;
  group: Group;
  members: Member[];
  membersLoading: boolean;
  currencies: Currency[];
  currentBalance?: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const account = state.kind === "edit" ? state.account : null;
  const isArchived = Boolean(account?.is_archived);
  const isEditing = Boolean(account);
  const initialType = account?.type || "bank";
  const defaults = accountTypeDefaults[initialType] || accountTypeDefaults.bank;
  const [type, setType] = useState(initialType);
  const [icon, setIcon] = useState(account?.icon || defaults.icon);
  const [color, setColor] = useState(account?.color || defaults.color);
  const [currency, setCurrency] = useState(account?.currency || group.base_currency || "EUR");
  const defaultOwnerUserId = account?.owner_user_id || members.find((member) => !member.is_blocked)?.user_id || members[0]?.user_id || "";
  const [ownerUserId, setOwnerUserId] = useState(defaultOwnerUserId);
  const [status, setStatus] = useState("");
  const [fieldErrors, setFieldErrors] = useState<AccountFormErrors>({});
  const [confirmDelete, setConfirmDelete] = useState(false);
  const animatedCloseRef = useRef(onClose);
  const requestAnimatedClose = () => animatedCloseRef.current();
  const selectedOwner = members.find((member) => member.user_id === ownerUserId);

  useEffect(() => {
    setOwnerUserId(account?.owner_user_id || members.find((member) => !member.is_blocked)?.user_id || members[0]?.user_id || "");
  }, [account?.owner_user_id, members]);

  const invalidateAccounts = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["accounts"] }),
      queryClient.invalidateQueries({ queryKey: queryKeys.summary }),
      queryClient.invalidateQueries({ queryKey: queryKeys.records }),
      queryClient.invalidateQueries({ queryKey: queryKeys.deletedRecords }),
    ]);
  };

  const saveMutation = useMutation({
    mutationFn: (payload: AccountPayload) =>
      account ? financeApi.updateAccount(group.id, account.id, payload) : financeApi.createAccount(group.id, payload),
    onSuccess: async () => {
      await invalidateAccounts();
      requestAnimatedClose();
    },
    onError: (error) => setStatus(error instanceof Error ? error.message : "Request failed"),
  });

  const deleteMutation = useMutation({
    mutationFn: () => financeApi.deleteAccount(group.id, account!.id),
    onSuccess: async () => {
      await invalidateAccounts();
      requestAnimatedClose();
    },
    onError: (error) => setStatus(error instanceof Error ? error.message : "Request failed"),
  });

  const restoreMutation = useMutation({
    mutationFn: () => financeApi.restoreAccount(group.id, account!.id),
    onSuccess: async () => {
      await invalidateAccounts();
      onClose();
    },
    onError: (error) => setStatus(error instanceof Error ? error.message : "Request failed"),
  });

  const setAccountType = (nextType: string) => {
    setType(nextType);
    if (isEditing) return;
    const nextDefaults = accountTypeDefaults[nextType] || accountTypeDefaults.bank;
    setIcon(nextDefaults.icon);
    setColor(nextDefaults.color);
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isArchived) return;
    setStatus("");
    const errors = validateAccountForm(event.currentTarget, isEditing);
    setFieldErrors(errors);
    if (hasAccountErrors(errors)) return;
    const { payload, error } = buildAccountPayload(event.currentTarget, type, icon, color, isEditing);
    if (error) {
      setStatus(error);
      return;
    }
    saveMutation.mutate(payload!);
  };

  return (
    <SidePanel ariaLabelledBy="accountModalTitle" className="account-modal" onClose={onClose}>
      {({ close }) => {
        animatedCloseRef.current = close;
        return (
          <>
        <Box className="records-modal-toolbar">
          <h2 id="accountModalTitle">
            {isArchived ? "Deleted account" : account ? "Edit account" : "Add account"}
            {account?.import_id ? (
              <Tooltip title="This account was created from an import.">
                <FileSpreadsheet className="record-import-icon" size={18} aria-label="Imported account" />
              </Tooltip>
            ) : null}
          </h2>
          <IconButton className="records-icon-button" type="button" aria-label="Close account dialog" title="Close" onClick={close}>
            <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M18 6 6 18"></path>
              <path d="m6 6 12 12"></path>
            </svg>
          </IconButton>
        </Box>
        <form className="records-form account-form" noValidate onSubmit={submit}>
          <Box className="account-controls-row">
            <IconColorPicker
              color={color}
              colorAriaLabel="Choose account color"
              colorTitle="Account color"
              disabled={isArchived}
              icon={icon}
              iconAriaLabel="Choose account icon"
              iconDataAttribute="account"
              iconInputId="accountIcon"
              pickerId="accountIconPicker"
              pickerLabel="Account icon"
              onColorChange={setColor}
              onIconChange={setIcon}
            />
            <Box className="records-field">
              <TextField
                required
                error={Boolean(fieldErrors.name)}
                helperText={fieldErrors.name}
                label="Name"
                name="name"
                placeholder="Main card"
                defaultValue={account?.name || ""}
                disabled={isArchived}
                fullWidth
                onChange={() => setFieldErrors((current) => ({ ...current, name: undefined }))}
              />
            </Box>
          </Box>
          <Box className="account-type-toggle">
            <ButtonGroup variant="outlined" aria-label="Account type">
              {accountTypes.map((option) => (
                <Button
                  key={option.value}
                  type="button"
                  variant={type === option.value ? "contained" : "outlined"}
                  disabled={isArchived}
                  onClick={() => setAccountType(option.value)}
                >
                  {option.label}
                </Button>
              ))}
            </ButtonGroup>
          </Box>
          <Box className="records-field">
            <TextField
              select
              required
              error={Boolean(fieldErrors.currency)}
              helperText={fieldErrors.currency}
              label="Currency"
              name="currency"
              value={currency}
              disabled={isArchived}
              onChange={(event) => {
                setCurrency(String(event.target.value));
                setFieldErrors((current) => ({ ...current, currency: undefined }));
              }}
            >
              {currencies.map((option) => (
                <MenuItem key={option.code} value={option.code}>
                  <span className="records-select-option">
                    <span className="currency-icon" aria-hidden="true">{option.symbol}</span>
                    <span>{option.code} · {option.name}</span>
                  </span>
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Box className="records-field">
            <TextField
              select
              required
              error={Boolean(fieldErrors.owner_user_id)}
              helperText={fieldErrors.owner_user_id}
              label="Owner"
              name="owner_user_id"
              value={ownerUserId}
              disabled={isArchived || membersLoading || !members.length}
              onChange={(event) => {
                setOwnerUserId(String(event.target.value));
                setFieldErrors((current) => ({ ...current, owner_user_id: undefined }));
              }}
              slotProps={{
                select: {
                  renderValue: () => selectedOwner ? <AccountOwnerOption member={selectedOwner} /> : "Select user",
                },
              }}
            >
              {members.map((member) => (
                <MenuItem key={member.user_id} value={member.user_id}>
                  <AccountOwnerOption member={member} />
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Box className="records-field">
            <TextField
              required
              error={Boolean(isEditing ? fieldErrors.current_amount : fieldErrors.initial_balance)}
              helperText={isEditing ? fieldErrors.current_amount : fieldErrors.initial_balance}
              label={isEditing ? "Current amount" : "Initial amount"}
              name={isEditing ? "current_amount" : "initial_balance"}
              inputMode="decimal"
              defaultValue={isEditing ? currentBalance || account?.initial_balance || "0.00" : "0.00"}
              disabled={isArchived}
              onChange={() => setFieldErrors((current) => ({ ...current, initial_balance: undefined, current_amount: undefined }))}
            />
          </Box>
          <Box className="records-modal-actions">
            {confirmDelete ? (
              <>
                <Button variant="outlined" type="button" disabled={deleteMutation.isPending} onClick={() => setConfirmDelete(false)}>
                  Cancel
                </Button>
                <Button variant="outlined" color="error" type="button" disabled={deleteMutation.isPending} onClick={() => deleteMutation.mutate()}>
                  Confirm delete
                </Button>
              </>
            ) : (
              <>
                {isArchived ? (
                  <Button variant="outlined" type="button" disabled={restoreMutation.isPending} onClick={() => restoreMutation.mutate()}>
                    Restore account
                  </Button>
                ) : null}
                {account && !isArchived ? (
                  <Button variant="outlined" color="error" type="button" disabled={deleteMutation.isPending} onClick={() => setConfirmDelete(true)}>
                    Delete account
                  </Button>
                ) : null}
                {!isArchived ? (
                  <Button variant="contained" type="submit" disabled={saveMutation.isPending}>
                    {account ? "Save changes" : "Create account"}
                  </Button>
                ) : null}
              </>
            )}
          </Box>
        </form>
        {status ? <p className="status error">{status}</p> : null}
          </>
        );
      }}
    </SidePanel>
  );
}

export function BankAccountsPage({ group, onBlockedAccess }: BankAccountsPageProps) {
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<AccountsMode>("active");
  const [modal, setModal] = useState<ModalState>({ kind: "closed" });
  const [confirmEmpty, setConfirmEmpty] = useState(false);
  const [emptyError, setEmptyError] = useState("");

  const accountsQuery = useQuery({ queryKey: queryKeys.accounts(group.id), queryFn: () => financeApi.accounts(group.id) });
  const summaryQuery = useQuery({ queryKey: ["summary", group.id], queryFn: () => financeApi.summary(group.id) });
  const membersQuery = useQuery({ queryKey: queryKeys.members(group.id), queryFn: () => financeApi.members(group.id) });
  const currenciesQuery = useQuery({ queryKey: queryKeys.currencies(group.id), queryFn: () => financeApi.currencies(group.id) });
  const accounts = accountsQuery.data || [];
  const members = membersQuery.data || [];
  const currencies = currenciesQuery.data?.length ? currenciesQuery.data : fallbackCurrencies;
  const membersById = new Map(members.map((member) => [member.user_id, member]));
  const activeAccounts = accounts.filter((account) => !account.is_archived);
  const deletedAccounts = accounts.filter((account) => account.is_archived);
  const visibleAccounts = mode === "deleted" ? deletedAccounts : activeAccounts;
  const balances = summaryQuery.data?.account_balances || {};

  useBlockedAccessGuard([accountsQuery.error, summaryQuery.error, membersQuery.error, currenciesQuery.error], onBlockedAccess);

  const emptyDeletedMutation = useMutation({
    mutationFn: () => financeApi.emptyDeletedAccounts(group.id),
    onSuccess: async () => {
      setConfirmEmpty(false);
      setEmptyError("");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["accounts"] }),
        queryClient.invalidateQueries({ queryKey: queryKeys.summary }),
      ]);
    },
    onError: (error) => setEmptyError(error instanceof Error ? error.message : "Request failed"),
  });

  return (
    <>
      <header className="topbar">
        <Box>
          <h1>{mode === "deleted" ? "Deleted accounts" : "Bank accounts"}</h1>
          <p>Accounts used for income and expense records.</p>
        </Box>
        {mode === "active" ? (
          <Box className="records-topbar-actions">
            <Button className="records-desktop-add-action" variant="contained" type="button" startIcon={<AddIcon fontSize="small" />} onClick={() => setModal({ kind: "create" })}>
              Add account
            </Button>
            <IconButton className="records-mobile-add-action" type="button" aria-label="Add account" title="Add account" onClick={() => setModal({ kind: "create" })}>
              <AddIcon />
            </IconButton>
          </Box>
        ) : null}
      </header>

      <section className="content">
        <Card className="records-panel accounts-panel">
          <CardContent className="records-panel-content">
            {mode === "active" ? (
              deletedAccounts.length ? (
                <Box className="records-toolbar">
                  <Box className="records-utility-actions">
                    <Button variant="outlined" type="button" startIcon={<DeleteOutlineIcon fontSize="small" />} onClick={() => setMode("deleted")}>
                      Deleted accounts
                    </Button>
                  </Box>
                </Box>
              ) : null
            ) : (
              <Box className="records-toolbar">
                <>
                  <Box className="records-primary-actions"></Box>
                  <Box className="records-utility-actions">
                    {deletedAccounts.length ? (
                      <Button variant="outlined" color="error" type="button" onClick={() => setConfirmEmpty(true)}>
                        Delete all
                      </Button>
                    ) : null}
                    <Button variant="outlined" type="button" onClick={() => setMode("active")}>
                      Back to accounts
                    </Button>
                  </Box>
                </>
              </Box>
            )}

            {accountsQuery.isLoading ? <p className="status">Loading accounts...</p> : null}
            {!accountsQuery.isLoading && !visibleAccounts.length ? <p className="status">{mode === "deleted" ? "No deleted accounts." : "No accounts yet."}</p> : null}

            {visibleAccounts.length ? (
              <Box className="category-list">
                <Box className="category-group account-trash-group">
                  <Box className="category-group-list">
                    {visibleAccounts.map((account) => (
                      <AccountRow
                        key={account.id}
                        account={account}
                        balance={balances[account.id] ?? account.initial_balance}
                        owner={account.owner_user_id ? membersById.get(account.owner_user_id) : undefined}
                        currencies={currencies}
                        onClick={() => setModal({ kind: "edit", account })}
                      />
                    ))}
                  </Box>
                </Box>
              </Box>
            ) : null}
          </CardContent>
        </Card>
      </section>

      {modal.kind !== "closed" ? (
        <AccountModal
          state={modal}
          group={group}
          members={members}
          membersLoading={membersQuery.isLoading}
          currencies={currencies}
          currentBalance={modal.kind === "edit" ? balances[modal.account.id] ?? modal.account.initial_balance : undefined}
          onClose={() => setModal({ kind: "closed" })}
        />
      ) : null}

      {confirmEmpty ? (
        <ConfirmDialog
          titleId="emptyAccountTrashTitle"
          title="Delete all accounts?"
          description="This permanently deletes every account currently in Deleted accounts. Accounts that have records cannot be permanently deleted."
          confirmLabel="Delete all"
          closeAriaLabel="Close delete all accounts dialog"
          pending={emptyDeletedMutation.isPending}
          error={emptyError}
          onConfirm={() => emptyDeletedMutation.mutate()}
          onClose={() => setConfirmEmpty(false)}
        />
      ) : null}
    </>
  );
}
